// src/Components/Luna/lunaModel.ts
// Luna power-ups, the pure half. The server owns the rule (every correct
// answer sends stardust to the equipped Luna, three in a row charge it and
// the next correct answer pays double); these helpers turn its `luna`
// payload into what the ring, the results card and the celebration show.
// Kept free of React and the DOM so it is unit tested on its own.

export interface LunaCombo {
  /** Correct answers in a row this activity. */
  count: number;
  /** 0..chargeNeeded; at chargeNeeded the next correct answer is doubled. */
  charge: number;
  chargeNeeded: number;
  /** The Luna is charged: the next correct answer pays double. */
  charged: boolean;
  /** This award was the doubled one. */
  boosted: boolean;
  multiplier: number;
}

export interface LunaEvolvedTo {
  stage: number;
  name: string;
  image: string | null;
  fromStage: number;
  fromName: string;
  fromImage: string | null;
}

/** The `luna` object every answer route returns. */
export interface LunaPayload {
  creatureId: string | null;
  name?: string;
  image?: string | null;
  stage?: number;
  xpGained: number;
  xp?: number;
  level?: number;
  inLevel?: number;
  nextLevelXp?: number;
  prevLevel?: number;
  leveledUp?: boolean;
  evolved?: boolean;
  evolvedTo?: LunaEvolvedTo | null;
  combo?: Partial<LunaCombo>;
  capped?: boolean;
}

/** What the ring shows. */
export interface LunaView {
  creatureId: string;
  name: string;
  image: string | null;
  stage: number;
  xp: number;
  level: number;
  inLevel: number;
  nextLevelXp: number;
  combo: LunaCombo;
}

export const CHARGE_NEEDED = 3;

export const EMPTY_COMBO: LunaCombo = {
  count: 0,
  charge: 0,
  chargeNeeded: CHARGE_NEEDED,
  charged: false,
  boosted: false,
  multiplier: 1,
};

/** XP needed to go from `level` to the next (same curve as the server). */
export function xpForNextLevel(level: number): number {
  return Math.max(100, Math.max(1, Math.floor(level) || 1) * 100);
}

/** Level and progress inside it from total XP. */
export function progressFromXp(totalXp: number): { level: number; inLevel: number; nextLevelXp: number } {
  let xp = Math.max(0, Number(totalXp) || 0);
  let level = 1;
  while (xp >= xpForNextLevel(level)) {
    xp -= xpForNextLevel(level);
    level += 1;
  }
  return { level, inLevel: xp, nextLevelXp: xpForNextLevel(level) };
}

/** 0..1 fill of the ring. */
export function ringFraction(view: Pick<LunaView, "inLevel" | "nextLevelXp"> | null | undefined): number {
  if (!view || !view.nextLevelXp) return 0;
  return Math.max(0, Math.min(1, view.inLevel / view.nextLevelXp));
}

/** Lit state of each charge pip around the ring. */
export function chargePips(combo: Partial<LunaCombo> | null | undefined): boolean[] {
  const need = combo?.chargeNeeded || CHARGE_NEEDED;
  const charge = Math.max(0, Math.min(need, Number(combo?.charge) || 0));
  return Array.from({ length: need }, (_, i) => i < charge);
}

function normCombo(c: Partial<LunaCombo> | null | undefined): LunaCombo {
  const need = c?.chargeNeeded || CHARGE_NEEDED;
  const charge = Math.max(0, Math.min(need, Number(c?.charge) || 0));
  return {
    count: Math.max(0, Number(c?.count) || 0),
    charge,
    chargeNeeded: need,
    charged: c?.charged ?? charge >= need,
    boosted: !!c?.boosted,
    multiplier: Number(c?.multiplier) || (c?.boosted ? 2 : 1),
  };
}

/**
 * The combo rule, mirrored for an instant reaction while the server answers:
 * three correct in a row charge the Luna, the next correct one is doubled
 * and the charge starts again; a miss clears it. The server's combo always
 * replaces this guess when its response lands.
 */
export function nextCombo(prev: Partial<LunaCombo> | null | undefined, correct: boolean): LunaCombo {
  const p = normCombo(prev);
  if (!correct) return { ...EMPTY_COMBO, chargeNeeded: p.chargeNeeded };
  const boosted = p.charge >= p.chargeNeeded;
  const charge = boosted ? 0 : p.charge + 1;
  return {
    count: p.count + 1,
    charge,
    chargeNeeded: p.chargeNeeded,
    charged: charge >= p.chargeNeeded,
    boosted,
    multiplier: boosted ? 2 : 1,
  };
}

/** Merge a server payload onto the current view. */
export function viewFromPayload(prev: LunaView | null, p: LunaPayload): LunaView | null {
  if (!p || !p.creatureId) return prev;
  const same = prev && prev.creatureId === p.creatureId;
  const xp = Number.isFinite(p.xp as number) ? Number(p.xp) : same ? prev!.xp + (p.xpGained || 0) : 0;
  const prog = progressFromXp(xp);
  return {
    creatureId: p.creatureId,
    name: p.name || (same ? prev!.name : "Your Luna"),
    image: p.image ?? (same ? prev!.image : null),
    stage: Number(p.stage) || (same ? prev!.stage : 1),
    xp,
    level: Number(p.level) || prog.level,
    inLevel: Number.isFinite(p.inLevel as number) ? Number(p.inLevel) : prog.inLevel,
    nextLevelXp: Number(p.nextLevelXp) || prog.nextLevelXp,
    combo: normCombo(p.combo),
  };
}

/** Short label for the "+N" that floats off the ring. */
export function gainLabel(xp: number): string {
  return `+${Math.max(0, Math.round(xp))}`;
}

/** Stardust particles for an award: more for bigger awards, never a swarm. */
export function burstCount(xp: number, boosted = false): number {
  const base = Math.round(6 + Math.min(3, Math.max(0, xp) / 8));
  return Math.min(11, base + (boosted ? 2 : 0));
}

/* ───────────── activity session (results card) ───────────── */

export interface LunaSession {
  creatureId: string | null;
  before: LunaView | null;
  after: LunaView | null;
  gained: number;
  answers: number;
  boosts: number;
  levelUps: number;
  evolutions: LunaEvolvedTo[];
}

export function startSession(view: LunaView | null): LunaSession {
  return {
    creatureId: view?.creatureId ?? null,
    before: view,
    after: view,
    gained: 0,
    answers: 0,
    boosts: 0,
    levelUps: 0,
    evolutions: [],
  };
}

/** Fold one award into the session. A swap mid-run starts the tally on the new Luna. */
export function addToSession(s: LunaSession, p: LunaPayload, viewAfter: LunaView | null): LunaSession {
  if (!p || !p.creatureId) return s;
  let base = s;
  if (s.creatureId && s.creatureId !== p.creatureId) base = startSession(null);
  const before =
    base.before && base.before.creatureId === p.creatureId
      ? base.before
      : viewAfter
        ? {
            ...viewAfter,
            ...progressFromXp(Math.max(0, viewAfter.xp - (p.xpGained || 0))),
            xp: Math.max(0, viewAfter.xp - (p.xpGained || 0)),
            stage: p.evolved && p.evolvedTo ? p.evolvedTo.fromStage : viewAfter.stage,
            image: p.evolved && p.evolvedTo ? p.evolvedTo.fromImage : viewAfter.image,
            name: p.evolved && p.evolvedTo ? p.evolvedTo.fromName : viewAfter.name,
          }
        : null;
  return {
    creatureId: p.creatureId,
    before,
    after: viewAfter,
    gained: base.gained + Math.max(0, p.xpGained || 0),
    answers: base.answers + 1,
    boosts: base.boosts + (p.combo?.boosted ? 1 : 0),
    levelUps: base.levelUps + (p.leveledUp ? Math.max(1, (Number(p.level) || 0) - (Number(p.prevLevel) || 0)) : 0),
    evolutions: p.evolved && p.evolvedTo ? [...base.evolutions, p.evolvedTo] : base.evolutions,
  };
}

export interface LunaGrowth {
  show: boolean;
  name: string;
  image: string | null;
  gained: number;
  boosts: number;
  fromLevel: number;
  toLevel: number;
  fromFraction: number;
  toFraction: number;
  levelUps: number;
  evolved: LunaEvolvedTo | null;
  nextLevelXp: number;
  inLevel: number;
}

/** What the "Your Luna grew" card shows. Hidden when nothing was earned. */
export function growthOf(s: LunaSession | null): LunaGrowth {
  const after = s?.after || null;
  const before = s?.before || after;
  const evolved = s && s.evolutions.length ? s.evolutions[s.evolutions.length - 1] : null;
  return {
    show: !!s && !!after && s.gained > 0,
    name: after?.name || "Your Luna",
    image: after?.image || null,
    gained: s?.gained || 0,
    boosts: s?.boosts || 0,
    fromLevel: before?.level || 1,
    toLevel: after?.level || 1,
    fromFraction: ringFraction(before),
    toFraction: ringFraction(after),
    levelUps: Math.max(0, (after?.level || 1) - (before?.level || 1)),
    evolved,
    nextLevelXp: after?.nextLevelXp || 100,
    inLevel: after?.inLevel || 0,
  };
}

/** Evolution thresholds of a definition (seed `evolvesAtLevels`, else `evolvesTo`). */
export function evolutionLevels(def: { evolvesAtLevels?: number[]; evolvesTo?: { levelRequired: number }[]; stageAssets?: { stage: number }[] } | null | undefined): number[] {
  if (!def) return [];
  const raw = Array.isArray(def.evolvesAtLevels) && def.evolvesAtLevels.length
    ? def.evolvesAtLevels
    : (def.evolvesTo || []).map((e) => Number(e.levelRequired));
  const levels = raw.filter((n) => Number.isFinite(n) && n > 1).sort((a, b) => a - b);
  const stages = def.stageAssets?.length ? Math.max(...def.stageAssets.map((s) => Number(s.stage) || 1)) : levels.length + 1;
  return levels.slice(0, Math.max(0, stages - 1));
}

/** Stage reached at a level. */
export function stageForLevel(def: Parameters<typeof evolutionLevels>[0], level: number): number {
  return 1 + evolutionLevels(def).filter((t) => level >= t).length;
}

/** The next evolution level above `level`, or null when fully evolved. */
export function nextEvolutionLevel(def: Parameters<typeof evolutionLevels>[0], level: number): number | null {
  return evolutionLevels(def).find((t) => t > level) ?? null;
}
